import {
  IconBellCog,
  IconBrain,
  IconBuilding,
  IconBuildingBank,
  IconBuildingCommunity,
  IconBuildingSkyscraper,
  IconCalendarEvent,
  IconCash,
  IconCashBanknote,
  IconClipboardList,
  IconClipboardText,
  IconCodeDots,
  IconCoin,
  IconContract,
  IconCreditCard,
  IconDeviceDesktopAnalytics,
  IconFileText,
  IconListCheck,
  IconListTree,
  IconPalette,
  IconReceipt,
  IconReportMoney,
  IconRobot,
  IconTags,
  IconUpload,
  IconUserCog,
  type TablerIcon,
} from '@tabler/icons-react';
import { defined } from '@teo/types';

import type { SelectOption } from '../../../hooks/usePropertyOptions';
import type { Read } from '../../../lib/readOf';
import { shouldShowPlaygroundNav } from '../../../pages/playground/playgroundNav';

export type NavChild = {
  label: string;
  to: string;
};

export type NavLeaf = {
  icon: TablerIcon;
  label: string;
  to: string;
  /**
   * A link drawn indented right under this one; the leaf itself stays a link. Rent Radar is the
   * only one today, and {@link isRadarSubLinkActive} says when it is current.
   */
  subLink?: NavChild;
};

export type NavGroup = {
  icon: TablerIcon;
  label: string;
  children: NavChild[];
};

export type RegularNavItem = NavLeaf | NavGroup;

export function isNavGroup(item: RegularNavItem): item is NavGroup {
  return 'children' in item && Boolean(item.children);
}

/**
 * Where the Rent Radar sub-link under Properties goes. `none` covers every case with nothing to
 * open: no rental property, a read that is not finished, and a read that failed.
 */
export type RadarNavTarget =
  { kind: 'none' } | { kind: 'one'; propertyId: string } | { kind: 'many' };

type RegularNavOptions = {
  isBoardMemberReadOnly: boolean;
  uploadFeatureUnlocked: boolean;
  radarTarget: RadarNavTarget;
};

/**
 * One rental property opens its radar at once. Several open the list to pick from. Nothing is
 * offered before the read has finished, so a half-loaded list never decides between the two. A
 * refresh that failed over loaded properties keeps the link: the list is the last one read.
 */
export function radarNavTarget(read: Read<readonly SelectOption[]>): RadarNavTarget {
  switch (read.kind) {
    case 'loading':
    case 'failed':
      return { kind: 'none' };
    case 'ready': {
      const [first, ...rest] = read.data;
      if (first === undefined) return { kind: 'none' };
      return rest.length === 0 ? { kind: 'one', propertyId: first.value } : { kind: 'many' };
    }
    default: {
      const _exhaustive: never = read;
      throw new Error(`Unhandled read kind: ${JSON.stringify(_exhaustive)}`);
    }
  }
}

function radarSubLink(target: RadarNavTarget): NavChild | undefined {
  switch (target.kind) {
    case 'none':
      return undefined;
    case 'one':
      return { label: 'Rent Radar', to: `/properties/${target.propertyId}/rent-radar` };
    case 'many':
      return { label: 'Rent Radar', to: '/properties/rent-radar' };
    default: {
      const _exhaustive: never = target;
      throw new Error(`Unhandled radar target: ${JSON.stringify(_exhaustive)}`);
    }
  }
}

const RADAR_PATH = /^\/properties\/(?:rent-radar|[^/]+\/rent-radar)$/;

/**
 * The sub-link is current on the property list (`/properties/rent-radar`) and on any one radar
 * (`/properties/<id>/rent-radar`), whichever of the two it points at.
 */
export function isRadarSubLinkActive(pathname: string): boolean {
  return RADAR_PATH.test(pathname);
}

/**
 * The regular nav in three runs, drawn with a divider between them: what the ownership is
 * (manage), what runs day to day (operate), and what is agreed for the future (plan).
 */
export type NavSectionKey = 'manage' | 'operate' | 'plan';

export interface NavSection {
  key: NavSectionKey;
  items: RegularNavItem[];
}

export function buildRegularNavSections({
  isBoardMemberReadOnly,
  uploadFeatureUnlocked,
  radarTarget,
}: RegularNavOptions): NavSection[] {
  // The server refuses a board member on Rent Radar, so the nav never offers it.
  const subLink = isBoardMemberReadOnly ? undefined : radarSubLink(radarTarget);
  const sections: NavSection[] = [
    {
      key: 'manage',
      items: [
        { icon: IconDeviceDesktopAnalytics, label: 'Dashboard', to: '/dashboard' },
        { icon: IconBuilding, label: 'Properties', to: '/properties', ...defined({ subLink }) },
        ...(!isBoardMemberReadOnly
          ? [
              { icon: IconBuildingBank, label: 'Ownerships', to: '/ownerships' },
              { icon: IconBuildingCommunity, label: 'Associations', to: '/associations' },
            ]
          : []),
        { icon: IconFileText, label: 'Documents', to: '/documents' },
      ],
    },
    {
      key: 'operate',
      items: [
        {
          icon: IconReceipt,
          label: 'Invoices',
          children: [
            { label: 'Accounts Payable', to: '/invoices' },
            ...(!isBoardMemberReadOnly
              ? [{ label: 'Accounts Receivable', to: '/ar-invoices' }]
              : []),
          ],
        },
        { icon: IconReportMoney, label: 'Financials', to: '/capital-management' },
        { icon: IconCalendarEvent, label: 'Calendar', to: '/calendar' },
        ...(uploadFeatureUnlocked ? [{ icon: IconUpload, label: 'Upload', to: '/upload' }] : []),
      ],
    },
    {
      key: 'plan',
      items: [
        ...(!isBoardMemberReadOnly
          ? [{ icon: IconContract, label: 'Contracts', to: '/contracts' }]
          : []),
        {
          icon: IconCashBanknote,
          label: 'Commitments',
          children: [
            { label: 'Income', to: '/commitments/income' },
            { label: 'Expense', to: '/expenses' },
            { label: 'Loans', to: '/expenses/loans' },
          ],
        },
        {
          icon: IconCoin,
          label: 'Budgets',
          children: [
            { label: 'Approved Budgets', to: '/budgets' },
            { label: 'Budget Planning', to: '/budget-planning' },
          ],
        },
      ],
    },
  ];
  return sections.filter((section) => section.items.length > 0);
}

/**
 * The admin nav in two runs, each under its own label. The screens of the new onboarding work
 * stay out of today's admin screens until the cut-over to the new flow.
 */
export type AdminNavSectionKey = 'new-onboarding' | 'admin';

export interface AdminNavSection {
  key: AdminNavSectionKey;
  label: string;
  items: NavLeaf[];
}

export function buildAdminNavSections(isAdmin: boolean): AdminNavSection[] {
  return isAdmin
    ? [
        {
          key: 'new-onboarding',
          label: 'New onboarding',
          items: [
            { icon: IconListCheck, label: 'Onboarding', to: '/admin/onboarding' },
            { icon: IconBuildingSkyscraper, label: 'Organizations', to: '/admin/organizations' },
            { icon: IconTags, label: 'Income Charge Mapping', to: '/admin/income-charge-mapping' },
            { icon: IconListTree, label: 'Chart Lines', to: '/admin/chart-lines' },
          ],
        },
        {
          key: 'admin',
          label: 'Admin',
          items: [
            { icon: IconUserCog, label: 'Users', to: '/users' },
            { icon: IconBellCog, label: 'Notifications', to: '/admin/notifications' },
            { icon: IconBrain, label: 'LLM Calls', to: '/admin/llm-calls' },
            { icon: IconRobot, label: 'Agent surface', to: '/admin/agent-surface' },
            { icon: IconCodeDots, label: 'Pipelines', to: '/admin/pipelines' },
            { icon: IconClipboardList, label: 'Task Queues', to: '/admin/tasks' },
            { icon: IconCash, label: 'General Ledger', to: '/admin/accounting' },
            { icon: IconCreditCard, label: 'Payments', to: '/admin/payments' },
            { icon: IconClipboardText, label: 'RFP Templates', to: '/admin/rfp-templates' },
            // Playground last: admin + allowlisted env only (fail-closed).
            ...(shouldShowPlaygroundNav(isAdmin)
              ? [{ icon: IconPalette, label: 'Playground', to: '/admin/playground' }]
              : []),
          ],
        },
      ]
    : [];
}
